import { PermissionSlug } from '../constants/featureFlags';

type ClinicPermissionAssignment = {
  clinic_id: string;
  permissions?: readonly string[];
};

type AssignedClinician = {
  id: string;
  user_id: string;
};

export function getAssignedClinicianUserId(
  assignedClinicianId: string | null | undefined,
  clinicians: AssignedClinician[],
  persistedAssignedClinicianUserId?: string | null
): string | null | undefined {
  if (!assignedClinicianId) return null;
  if (persistedAssignedClinicianUserId !== undefined) {
    return persistedAssignedClinicianUserId ?? undefined;
  }
  return clinicians.find(clinician => clinician.id === assignedClinicianId)
    ?.user_id;
}

function hasActiveClinicMembership(
  activeClinicIds: readonly string[] | undefined,
  clinicId: string | null | undefined
): boolean {
  return Boolean(clinicId && activeClinicIds?.includes(clinicId));
}

function hasClinicRoleEntry(
  clinicPermissions: readonly ClinicPermissionAssignment[] | undefined,
  clinicId: string | null | undefined
): boolean {
  return Boolean(
    clinicId && clinicPermissions?.some(clinic => clinic.clinic_id === clinicId)
  );
}

function hasClinicPermission(
  clinicPermissions: readonly ClinicPermissionAssignment[] | undefined,
  clinicId: string | null | undefined,
  permission: string
): boolean {
  return Boolean(
    clinicId &&
    clinicPermissions?.some(
      clinic =>
        clinic.clinic_id === clinicId &&
        clinic.permissions?.includes(permission)
    )
  );
}

/**
 * Continue goals from the previous episode (EMR-2754). `goals:carry-forward`
 * is clinic-scoped, so it counts only when held at the patient's clinic; the
 * server re-checks the same way. Roger Admin passes.
 */
export function canCarryForwardGoals({
  clinicId,
  clinicPermissions,
  hasAgencyPermission,
}: {
  clinicId: string | null | undefined;
  clinicPermissions: readonly ClinicPermissionAssignment[] | undefined;
  hasAgencyPermission?: (permission: string) => boolean;
}): boolean {
  if (clinicId && hasAgencyPermission?.(PermissionSlug.ADMIN_SUPER_USER)) {
    return true;
  }
  return hasClinicPermission(
    clinicPermissions,
    clinicId,
    PermissionSlug.GOALS_CARRY_FORWARD
  );
}

type ChartEditPermissionArgs = {
  assignedClinicianUserId: string | null | undefined;
  currentUserId: string | undefined;
  clinicId: string | null | undefined;
  clinicPermissions: readonly ClinicPermissionAssignment[] | undefined;
  activeClinicIds?: readonly string[];
  /** The store's `hasPermission`, which already passes Roger Admin. */
  hasAgencyPermission?: (permission: string) => boolean;
};

export type EpisodeEditorCapabilities = {
  canEditDates: boolean;
  canEditStatus: boolean;
  canEditPreClaimReview: boolean;
  canEditNotes: boolean;
};

/**
 * Assignment is the ordinary chart-edit boundary. An unassigned chart needs
 * the dedicated clinic or agency permission; missing clinic data stays
 * read-only because the permission scope cannot be established.
 */
export function canEditChartContent({
  assignedClinicianUserId,
  currentUserId,
  clinicId,
  clinicPermissions,
  activeClinicIds,
  hasAgencyPermission,
}: ChartEditPermissionArgs): boolean {
  // Roger Admin skips the membership and grant checks below. The server still
  // needs the chart's clinic, so this does too.
  if (clinicId && hasAgencyPermission?.(PermissionSlug.ADMIN_SUPER_USER)) {
    return true;
  }
  if (assignedClinicianUserId === undefined || currentUserId === undefined) {
    return false;
  }
  if (!hasActiveClinicMembership(activeClinicIds, clinicId)) return false;
  if (assignedClinicianUserId === currentUserId) return true;
  if (assignedClinicianUserId !== null) {
    return hasClinicPermission(
      clinicPermissions,
      clinicId,
      PermissionSlug.CHART_EDIT_UNASSIGNED
    );
  }
  return (
    (hasAgencyPermission?.(PermissionSlug.CHART_EDIT_UNASSIGNED_AGENCY) ===
      true &&
      hasClinicRoleEntry(clinicPermissions, clinicId)) ||
    hasClinicPermission(
      clinicPermissions,
      clinicId,
      PermissionSlug.CHART_EDIT_UNASSIGNED
    )
  );
}

function canEditEpisodeManager({
  assignedClinicianUserId,
  currentUserId,
  clinicId,
  clinicPermissions,
  activeClinicIds,
  hasAgencyPermission,
  allowAgencyPermission,
}: ChartEditPermissionArgs & { allowAgencyPermission: boolean }): boolean {
  // Roger Admin skips the membership and grant checks below. The server still
  // needs the chart's clinic, so this does too.
  if (clinicId && hasAgencyPermission?.(PermissionSlug.ADMIN_SUPER_USER)) {
    return true;
  }
  if (assignedClinicianUserId === undefined || currentUserId === undefined) {
    return false;
  }
  if (!hasActiveClinicMembership(activeClinicIds, clinicId)) return false;
  if (assignedClinicianUserId === currentUserId) return true;
  if (
    allowAgencyPermission &&
    hasAgencyPermission?.(
      PermissionSlug.EPISODE_MANAGER_EDIT_UNASSIGNED_AGENCY
    ) === true
  ) {
    return true;
  }
  return hasClinicPermission(
    clinicPermissions,
    clinicId,
    PermissionSlug.EPISODE_MANAGER_EDIT_UNASSIGNED
  );
}

/** Episode Manager fields retain the existing agency/clinic behavior by default. */
export function canEditEpisodeManagerFields(
  args: ChartEditPermissionArgs,
  options: { allowAgencyPermission?: boolean } = {}
): boolean {
  return canEditEpisodeManager({
    ...args,
    allowAgencyPermission: options.allowAgencyPermission ?? true,
  });
}

/**
 * The agency-wide Episode Manager grant opens only the date fields. Existing
 * owners and clinic-scoped Episode Managers keep the editor controls they had.
 */
export function getEpisodeEditorCapabilities(
  args: ChartEditPermissionArgs
): EpisodeEditorCapabilities | null {
  if (!canEditEpisodeManagerFields(args)) {
    return null;
  }
  const hasExistingEditorAccess = canEditEpisodeManagerFields(args, {
    allowAgencyPermission: false,
  });
  return {
    canEditDates: true,
    canEditStatus: hasExistingEditorAccess,
    canEditPreClaimReview: hasExistingEditorAccess,
    canEditNotes: hasExistingEditorAccess,
  };
}

/**
 * The editor for a holder of `episode:reopen` who has no other editor access
 * (EMR-2433): every field control off, so only Mark incomplete / completed show.
 * Pass this explicitly. An omitted `capabilities` prop means full access.
 */
export const REOPEN_ONLY_CAPABILITIES: EpisodeEditorCapabilities = {
  canEditDates: false,
  canEditStatus: false,
  canEditPreClaimReview: false,
  canEditNotes: false,
};

/**
 * The editor on a chart still charted in the External EMR (Take Over v2).
 * Dates and status are care, so they lock. Pre-claim review is billing and
 * notes are office work, so they stay as the caller's access allows.
 */
export function lockEpisodeCareFields(
  capabilities: EpisodeEditorCapabilities,
  chartLocked: boolean
): EpisodeEditorCapabilities {
  if (!chartLocked) return capabilities;
  return { ...capabilities, canEditDates: false, canEditStatus: false };
}

/** TasksTab scheduling controls accept the agency-wide Scheduling grant. */
export function canEditEpisodeManagerTask(
  args: ChartEditPermissionArgs
): boolean {
  return canEditEpisodeManager({ ...args, allowAgencyPermission: true });
}

export type { ClinicPermissionAssignment };
